import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Card,
  Row,
  Col,
  Typography,
  Space,
  Alert,
  Progress,
  Steps,
  message,
  Select,
  InputNumber,
} from "antd";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  ApartmentOutlined,
  MailOutlined,
  PhoneOutlined,
  NumberOutlined,
  BankOutlined,
  ArrowRightOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  SecurityScanOutlined,
  EditOutlined,
  SendOutlined,
  ClockCircleOutlined,
  SafetyCertificateOutlined,
  StarOutlined,
  RocketOutlined,
  LoadingOutlined,
  PushpinOutlined,
  EnvironmentOutlined,
  GiftOutlined,
} from "@ant-design/icons";
import { ValidationRules } from "../createCompanyPublic/validationRules";
import companyService from "../../../../../services/company/company.service";
import { showNotification } from "../createCompanyPublic/registrationHelpers";
import ReCAPTCHA from "react-google-recaptcha";
import ilIlceData from "../../../../../core/data/json/il-ilce.json";
import "./register.css";
import referralService from "../../../../../services/referral/referral.service";
import { ReferralRewardInfo } from "../../../../../types/referral.types";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Step } = Steps;
const { Option } = Select;

/** Telefonu +90 ile başlayacak şekilde normalize eder (input'tan gelen sadece rakamları +90 ile birleştirir). */
function normalizePhoneWithCountryCode(phone: string | undefined): string {
  if (phone == null || String(phone).trim() === "") return "+90";
  const digits = String(phone).replace(/\D/g, "");
  if (digits.length === 0) return "+90";
  // Input'tan sadece rakamlar geliyor (5XX XXX XX XX formatından), direkt +90 ekle
  return "+90" + digits;
}

interface CompanyFormProps {
  form: any;
  onFinish: (values: any) => Promise<void>;
  loading: boolean;
}

const CompanyForm: React.FC<CompanyFormProps> = ({
  form,
  onFinish,
  loading,
}) => {
  const [otpStep, setOtpStep] = useState<"form" | "otp-sent" | "otp-verified">(
    "form"
  );
  const [otpLoading, setOtpLoading] = useState(false);
  const [companyData, setCompanyData] = useState<any>(null);
  const [countdown, setCountdown] = useState(0);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [formProgress, setFormProgress] = useState(0);
  const [validationStatus, setValidationStatus] = useState<{
    [key: string]: "success" | "error" | "validating" | "";
  }>({});
  const [otpValues, setOtpValues] = useState<string[]>([
    "",
    "",
    "",
    "",
    "",
    "",
  ]);
  const [cities, setCities] = useState<
    { il_adi: string; plaka_kodu: string }[]
  >([]);
  const [districts, setDistricts] = useState<
    { ilce_adi: string; ilce_kodu: string }[]
  >([]);
  const [selectedCity, setSelectedCity] = useState<string | undefined>(
    form.getFieldValue("city")
  );

  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);
  const [recaptchaExpired, setRecaptchaExpired] = useState(false);

  // reCAPTCHA callback functions
  const onRecaptchaChange = (value: string | null) => {
    setRecaptchaValue(value);
    setRecaptchaExpired(false);
    if (value) {
      // reCAPTCHA doğrulandı
      handleFieldValidation("recaptcha", "success");
    } else {
      handleFieldValidation("recaptcha", "error");
    }
  };

  const onRecaptchaExpired = () => {
    setRecaptchaValue(null);
    setRecaptchaExpired(true);
    handleFieldValidation("recaptcha", "error");
    showNotification(
      "warning",
      "reCAPTCHA Süresi Doldu",
      "Lütfen reCAPTCHA doğrulamasını yenileyin."
    );
  };

  // Form progress tracking
  const trackFormProgress = () => {
    const values = form.getFieldsValue();
    const requiredFields = [
      "name",
      "email",
      "phone",
      "city",
      "district",
      "zipCode",
      "taxNumber",
      "taxOffice",
      "address",
    ];
    const filledFields = requiredFields.filter(
      (field) => values[field] && values[field].toString().trim()
    );
    const progress = Math.round(
      (filledFields.length / requiredFields.length) * 100
    );
    setFormProgress(progress);
  };

  // Countdown timer for resend OTP
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // İlleri yükle
  useEffect(() => {
    if (ilIlceData && ilIlceData.data) {
      const cityList = ilIlceData.data.map((city) => ({
        il_adi: city.il_adi,
        plaka_kodu: parseInt(city.plaka_kodu.trim()).toString(),
      }));
      setCities(cityList);
    }

    // Form değişikliklerini dinle
    const formCity = form.getFieldValue("city");
    if (formCity && formCity !== selectedCity) {
      setSelectedCity(formCity);
    }
  }, [form, selectedCity]);

  // Şehir değiştiğinde ilçeleri güncelle
  useEffect(() => {
    if (selectedCity) {
      const cityData = ilIlceData.data.find(
        (city) => city.il_adi === selectedCity
      );
      if (cityData && cityData.ilceler) {
        setDistricts(cityData.ilceler);

        // İlçe değerini koru, sadece şehir değiştiyse sıfırla
        const currentDistrict = form.getFieldValue("district");
        const isDistrictValid = cityData.ilceler.some(
          (d) => d.ilce_adi === currentDistrict
        );

        if (!isDistrictValid) {
          form.setFieldsValue({ district: undefined });
        }
      } else {
        setDistricts([]);
      }
    } else {
      setDistricts([]);
    }
  }, [selectedCity, form]);

  // Şehir değişikliğini yönet
  const handleCityChange = (value: string) => {
    setSelectedCity(value);
  };

  // İlk form submit - OTP gönderme
  const handleInitialSubmit = async (values: any) => {
    if (!recaptchaValue) {
      showNotification(
        "error",
        "🤖 reCAPTCHA Gerekli",
        "Lütfen robot olmadığınızı doğrulayın."
      );
      return;
    }

    try {
      setOtpLoading(true);

      // Telefonu her zaman +90 ile kaydet
      const phoneNormalized = normalizePhoneWithCountryCode(values.phone);
      const valuesWithPhone = { ...values, phone: phoneNormalized };

      // Form verilerini kaydet
      setCompanyData(valuesWithPhone);

      // Referans kodunu şirket oluşturulmadan önce doğrula ve localStorage'a kaydet
      if (values.referralCode && values.referralCode.trim()) {
        const code = values.referralCode.trim().toUpperCase();
        
        try {
          // Referans kodunu doğrula (şirket oluşturulmadan önce)
          console.log('🔍 Referans kodu doğrulanıyor (şirket oluşturulmadan önce):', code);
          const rewardInfo = await referralService.getRewardInfo(code);
          
          if (rewardInfo) {
            // Kod geçerli - kod ve bilgilerini localStorage'a kaydet
            const referralData = {
              code: code,
              rewardInfo: rewardInfo
            };
            localStorage.setItem('pendingReferralCode', JSON.stringify(referralData));
            console.log('✅ Referans kodu doğrulandı ve localStorage\'a kaydedildi:', {
              code: code,
              rewardType: rewardInfo.rewardType,
              rewardValue: rewardInfo.rewardValue,
              description: rewardInfo.description
            });
          } else {
            // Geçersiz kod - OTP gönderme işlemini durdur
            localStorage.removeItem('pendingReferralCode');
            showNotification(
              "error",
              "⚠️ Geçersiz Referans Kodu",
              "Girdiğiniz referans kodu geçersiz veya süresi dolmuş. Lütfen geçerli bir kod girin veya kodu boş bırakın."
            );
            console.log('❌ Geçersiz referans kodu - OTP gönderilmedi');
            setOtpLoading(false);
            return; // OTP gönderme işlemini durdur
          }
        } catch (error) {
          console.error('❌ Referans kodu doğrulama hatası:', error);
          // Hata durumunda kullanıcıya bilgi ver ve OTP gönderme işlemini durdur
          showNotification(
            "error",
            "❌ Referans Kodu Doğrulama Hatası",
            "Referans kodu doğrulanırken bir hata oluştu. Lütfen tekrar deneyin veya kodu boş bırakın."
          );
          setOtpLoading(false);
          return; // OTP gönderme işlemini durdur
        }
      } else {
        localStorage.removeItem('pendingReferralCode');
        console.log('🗑️ Referans kodu yok, localStorage temizlendi');
      }

      // OTP gönder (+90 ile normalize edilmiş numaraya)
      const response = await companyService.sendOtp(phoneNormalized);

      // SessionToken'ı kaydet
      if (response.sessionToken) {
        setSessionToken(response.sessionToken);
      }

      setOtpStep("otp-sent");
      setCountdown(180); // 180 saniye bekleme süresi
      showNotification(
        "success",
        "📱 Doğrulama Kodu Gönderildi!",
        `${phoneNormalized} numarasına doğrulama kodu gönderildi.`
      );
    } catch (error: any) {
      showNotification(
        "error",
        "❌ Hata Oluştu",
        "Doğrulama kodu gönderilirken bir hata oluştu. Lütfen telefon numaranızı kontrol edin."
      );
    } finally {
      setOtpLoading(false);
    }
  };

  // OTP doğrulama ve şirket oluşturma
  const handleOtpSubmit = async (values: any) => {
    // OTP değerini otpValues'dan al
    const otpString = otpValues.join("");

    if (!otpString || otpString.length !== 6) {
      showNotification(
        "error",
        "⚠️ Geçersiz Kod",
        "Lütfen 6 haneli doğrulama kodunu girin."
      );
      return;
    }

    if (!/^\d{6}$/.test(otpString)) {
      showNotification(
        "error",
        "⚠️ Geçersiz Kod",
        "Doğrulama kodu sadece rakamlardan oluşmalıdır."
      );
      return;
    }

    try {
      // Şirket oluşturma işlemini parent component'e gönder
      const finalData = {
        ...companyData,
        otp: otpString,
        sessionToken: sessionToken,
      };

      // Parent component'e await ile gönder - hata durumunda catch'e düşecek
      await onFinish(finalData);
      
      // Eğer buraya kadar geldiyse başarılı demektir
      setOtpStep("otp-verified");
    } catch (error: any) {
      // OTP hatası durumunda input'ları temizle
      setOtpValues(["", "", "", "", "", ""]);
      form.setFieldsValue({ otp: "" });
      
      // OTP step'ini "otp-sent" durumunda tut (kullanıcı tekrar deneyebilsin)
      // setOtpStep değiştirmiyoruz, zaten "otp-sent" durumunda
      
      // Spesifik hata mesajı göster
      if (error?.response?.data?.message?.includes("OTP") || error?.response?.data?.message?.includes("kod")) {
        showNotification(
          "error",
          "🔐 Doğrulama Kodu Hatalı",
          "Girdiğiniz doğrulama kodu hatalı veya süresi dolmuş. Lütfen tekrar deneyin."
        );
      } else {
        showNotification(
          "error",
          "❌ Hata Oluştu",
          "Doğrulama kodu kontrol edilirken bir hata oluştu. Lütfen tekrar deneyin."
        );
      }
    }
  };

  // OTP tekrar gönderme
  const handleResendOtp = async () => {
    if (countdown > 0) return;

    try {
      setOtpLoading(true);
      const response = await companyService.sendOtp(companyData.phone);

      // Yeni sessionToken'ı kaydet
      if (response.sessionToken) {
        setSessionToken(response.sessionToken);
      }

      setCountdown(180);
      showNotification(
        "success",
        "🔄 Kod Tekrar Gönderildi!",
        "Yeni doğrulama kodu gönderildi."
      );
    } catch (error: any) {
      showNotification(
        "error",
        "❌ Hata Oluştu",
        "Kod tekrar gönderilirken bir hata oluştu."
      );
    } finally {
      setOtpLoading(false);
    }
  };

  // Telefon numarasını düzenleme
  const handleEditPhone = () => {
    setOtpStep("form");
    setCompanyData(null);
    setCountdown(0);
    setSessionToken(null);
    setOtpValues(["", "", "", "", "", ""]);
    form.setFieldsValue({ otp: "" });
  };

  // Field validation handler
  const handleFieldValidation = (
    fieldName: string,
    status: "success" | "error" | "validating" | ""
  ) => {
    setValidationStatus((prev) => ({
      ...prev,
      [fieldName]: status,
    }));
  };

  // Form adımına göre içerik render etme
  const renderFormContent = () => {
    if (otpStep === "form") {
      return (
        <>
          <div
            className="step-header"
            style={{
              background: "white",
              color: "#2d3748",
              padding: "24px 0",
              marginBottom: "24px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: "64px",
                height: "64px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                fontSize: "24px",
                color: "white",
              }}
            >
              <ApartmentOutlined />
            </div>
            <Title
              level={2}
              // className="step-title"
              style={{ color: "#2d3748 !important", marginBottom: "8px" }}
            >
              Şirketinizi Kaydedin
            </Title>
            <Paragraph
              // className="step-description"
              style={{
                color: "#4a5568 !important",
                fontSize: "16px",
                marginBottom: "0",
              }}
            >
              Başarının kapılarını aralayın! Şirket bilgilerinizi güvenle
              kaydedin ve Dinox'un tüm avantajlarından yararlanmaya hemen
              başlayın.
            </Paragraph>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleInitialSubmit}
            size="large"
            requiredMark="optional"
            className="modern-form"
            onValuesChange={trackFormProgress}
          >
            <Row gutter={[24, 0]}>
              <Col xs={24}>
                <div
                  className={`modern-form-item ${
                    focusedField === "name" ? "focused" : ""
                  } ${validationStatus.name === "success" ? "success" : ""}`}
                >
                  <Form.Item
                    name="name"
                    label={
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontWeight: 600,
                          fontSize: "15px",
                          color: "#2d3748",
                          marginBottom: "8px",
                        }}
                      >
                        <ApartmentOutlined
                          style={{ color: "#667eea", fontSize: "16px" }}
                        />
                        Şirket Adı
                        <span
                          style={{
                            color: "#ff6b6b",
                            fontWeight: 700,
                            marginLeft: "4px",
                          }}
                        >
                          *
                        </span>
                      </span>
                    }
                    rules={ValidationRules.companyName}
                    hasFeedback
                    validateStatus={validationStatus.name}
                  >
                    <Input
                      placeholder="Örn: Teknoloji Çözümleri A.Ş."
                      showCount
                      maxLength={100}
                      onFocus={() => setFocusedField("name")}
                      onBlur={() => setFocusedField(null)}
                      style={{
                        borderRadius: "12px",
                        border: "2px solid #e2e8f0",
                        padding: "12px 16px",
                        fontSize: "16px",
                        background: "white",
                      }}
                      suffix={
                        validationStatus.name === "success" ? (
                          <CheckCircleOutlined
                            style={{ color: "#11998e", fontSize: "16px" }}
                          />
                        ) : null
                      }
                    />
                  </Form.Item>
                </div>
              </Col>

              <Col xs={24} lg={12}>
                <div
                  className={`modern-form-item ${
                    focusedField === "email" ? "focused" : ""
                  } ${validationStatus.email === "success" ? "success" : ""}`}
                >
                  <Form.Item
                    name="email"
                    label={
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontWeight: 600,
                          fontSize: "15px",
                          color: "#2d3748",
                        }}
                      >
                        <MailOutlined
                          style={{ color: "#667eea", fontSize: "16px" }}
                        />
                        Kurumsal E-posta
                        <span
                          style={{
                            color: "#ff6b6b",
                            fontWeight: 700,
                            marginLeft: "4px",
                          }}
                        >
                          *
                        </span>
                      </span>
                    }
                    rules={ValidationRules.email}
                    hasFeedback
                    validateStatus={validationStatus.email}
                  >
                    <Input
                      placeholder="info@sirketiniz.com"
                      type="email"
                      onFocus={() => setFocusedField("email")}
                      onBlur={() => setFocusedField(null)}
                      style={{
                        borderRadius: "12px",
                        border: "2px solid #e2e8f0",
                        padding: "12px 16px",
                        fontSize: "16px",
                        background: "white",
                      }}
                      suffix={
                        validationStatus.email === "success" ? (
                          <CheckCircleOutlined
                            style={{ color: "#11998e", fontSize: "16px" }}
                          />
                        ) : null
                      }
                    />
                  </Form.Item>
                </div>
              </Col>

              <Col xs={24} lg={12}>
                <div
                  className={`modern-form-item ${
                    focusedField === "phone" ? "focused" : ""
                  } ${validationStatus.phone === "success" ? "success" : ""}`}
                >
                  <Form.Item
                    name="phone"
                    label={
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontWeight: 600,
                          fontSize: "15px",
                          color: "#2d3748",
                        }}
                      >
                        <PhoneOutlined
                          style={{ color: "#667eea", fontSize: "16px" }}
                        />
                        Telefon Numarası
                        <span
                          style={{
                            color: "#ff6b6b",
                            fontWeight: 700,
                            marginLeft: "4px",
                          }}
                        >
                          *
                        </span>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="tooltip-primary">
                              SMS ile doğrulama kodu gönderilecektir
                            </Tooltip>
                          }
                        >
                          <InfoCircleOutlined
                            style={{
                              color: "#4facfe",
                              fontSize: "14px",
                              marginLeft: "4px",
                              cursor: "help",
                            }}
                          />
                        </OverlayTrigger>
                      </span>
                    }
                    rules={ValidationRules.phone}
                    hasFeedback
                    validateStatus={validationStatus.phone}
                  >
                    <Input.Group compact style={{ display: "flex" }}>
                      <Input
                        value="+90"
                        readOnly
                        disabled
                        style={{
                          width: "70px",
                          textAlign: "center",
                          fontWeight: "bold",
                          borderRadius: "12px 0 0 12px",
                          border: "2px solid #e2e8f0",
                          borderRight: "1px solid #cbd5e0",
                          background: "#f7fafc",
                          color: "#2d3748",
                          fontSize: "16px",
                          padding: "12px 8px",
                          cursor: "not-allowed",
                        }}
                      />
                      <Input
                        placeholder="5XX XXX XX XX"
                        maxLength={10}
                        onFocus={() => setFocusedField("phone")}
                        onBlur={() => setFocusedField(null)}
                        onChange={(e) => {
                          // Sadece rakam girmesine izin ver
                          const value = e.target.value.replace(/\D/g, "");
                          // Maksimum 10 haneli (5XX XXX XX XX)
                          const limitedValue = value.slice(0, 10);
                          form.setFieldsValue({ phone: limitedValue });
                        }}
                        style={{
                          borderRadius: "0 12px 12px 0",
                          border: "2px solid #e2e8f0",
                          borderLeft: "none",
                          padding: "12px 16px",
                          fontSize: "16px",
                          background: "white",
                          flex: 1,
                        }}
                        suffix={
                          validationStatus.phone === "success" ? (
                            <CheckCircleOutlined
                              style={{ color: "#11998e", fontSize: "16px" }}
                            />
                          ) : null
                        }
                      />
                    </Input.Group>
                  </Form.Item>
                </div>
              </Col>

              <Col xs={24} lg={12}>
                <div
                  className={`modern-form-item ${
                    focusedField === "city" ? "focused" : ""
                  } ${validationStatus.city === "success" ? "success" : ""}`}
                >
                  <Form.Item
                    name="city"
                    label={
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontWeight: 600,
                          fontSize: "15px",
                          color: "#2d3748",
                        }}
                      >
                        <EnvironmentOutlined
                          style={{ color: "#667eea", fontSize: "16px" }}
                        />
                        İl/Şehir
                        <span
                          style={{
                            color: "#ff6b6b",
                            fontWeight: 700,
                            marginLeft: "4px",
                          }}
                        >
                          *
                        </span>
                      </span>
                    }
                    rules={[{ required: true, message: "İl/Şehir zorunludur" }]}
                    hasFeedback
                    validateStatus={validationStatus.city}
                  >
                    <Select
                      placeholder="İl/Şehir seçin"
                      showSearch
                      onChange={handleCityChange}
                      onFocus={() => setFocusedField("city")}
                      onBlur={() => setFocusedField(null)}
                      filterOption={(input, option) =>
                        (option?.children as unknown as string)
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      style={{
                        borderRadius: "12px",
                        fontSize: "16px",
                      }}
                      size="large"
                      suffixIcon={
                        validationStatus.city === "success" ? (
                          <CheckCircleOutlined
                            style={{ color: "#11998e", fontSize: "16px" }}
                          />
                        ) : undefined
                      }
                    >
                      {cities.map((city) => (
                        <Option key={city.plaka_kodu} value={city.il_adi}>
                          {city.il_adi}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              </Col>

              <Col xs={24} lg={12}>
                <div
                  className={`modern-form-item ${
                    focusedField === "district" ? "focused" : ""
                  } ${
                    validationStatus.district === "success" ? "success" : ""
                  }`}
                >
                  <Form.Item
                    name="district"
                    label={
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontWeight: 600,
                          fontSize: "15px",
                          color: "#2d3748",
                        }}
                      >
                        <EnvironmentOutlined
                          style={{ color: "#667eea", fontSize: "16px" }}
                        />
                        İlçe
                        <span
                          style={{
                            color: "#ff6b6b",
                            fontWeight: 700,
                            marginLeft: "4px",
                          }}
                        >
                          *
                        </span>
                      </span>
                    }
                    rules={[{ required: true, message: "İlçe zorunludur" }]}
                    hasFeedback
                    validateStatus={validationStatus.district}
                  >
                    <Select
                      placeholder="İlçe seçin"
                      showSearch
                      disabled={!selectedCity}
                      onFocus={() => setFocusedField("district")}
                      onBlur={() => setFocusedField(null)}
                      filterOption={(input, option) =>
                        (option?.children as unknown as string)
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      style={{
                        borderRadius: "12px",
                        fontSize: "16px",
                      }}
                      size="large"
                      suffixIcon={
                        validationStatus.district === "success" ? (
                          <CheckCircleOutlined
                            style={{ color: "#11998e", fontSize: "16px" }}
                          />
                        ) : undefined
                      }
                    >
                      {districts.map((district) => (
                        <Option
                          key={district.ilce_kodu}
                          value={district.ilce_adi}
                        >
                          {district.ilce_adi}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              </Col>

              <Col xs={24} lg={12}>
                <div
                  className={`modern-form-item ${
                    focusedField === "zipCode" ? "focused" : ""
                  } ${validationStatus.zipCode === "success" ? "success" : ""}`}
                >
                  <Form.Item
                    name="zipCode"
                    label={
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontWeight: 600,
                          fontSize: "15px",
                          color: "#2d3748",
                        }}
                      >
                        <PushpinOutlined
                          style={{ color: "#667eea", fontSize: "16px" }}
                        />
                        Posta Kodu
                        <span
                          style={{
                            color: "#ff6b6b",
                            fontWeight: 700,
                            marginLeft: "4px",
                          }}
                        >
                          *
                        </span>
                      </span>
                    }
                    rules={[
                      { required: true, message: "Posta kodu zorunludur" },
                      {
                        pattern: /^\d{5}$/,
                        message: "Posta kodu 5 haneli bir sayı olmalıdır.",
                      },
                    ]}
                    hasFeedback
                    validateStatus={validationStatus.zipCode}
                  >
                    <Input
                      placeholder="Örn: 34700"
                      maxLength={5}
                      onFocus={() => setFocusedField("zipCode")}
                      onBlur={() => setFocusedField(null)}
                      style={{
                        borderRadius: "12px",
                        border: "2px solid #e2e8f0",
                        padding: "12px 16px",
                        fontSize: "16px",
                        background: "white",
                      }}
                      suffix={
                        validationStatus.zipCode === "success" ? (
                          <CheckCircleOutlined
                            style={{ color: "#11998e", fontSize: "16px" }}
                          />
                        ) : null
                      }
                    />
                  </Form.Item>
                </div>
              </Col>

              <Col xs={24} lg={12}>
                <div
                  className={`modern-form-item ${
                    focusedField === "taxNumber" ? "focused" : ""
                  } ${
                    validationStatus.taxNumber === "success" ? "success" : ""
                  }`}
                >
                  <Form.Item
                    name="taxNumber"
                    label={
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontWeight: 600,
                          fontSize: "15px",
                          color: "#2d3748",
                        }}
                      >
                        <NumberOutlined
                          style={{ color: "#667eea", fontSize: "16px" }}
                        />
                        Vergi Numarası
                        <span
                          style={{
                            color: "#ff6b6b",
                            fontWeight: 700,
                            marginLeft: "4px",
                          }}
                        >
                          *
                        </span>
                      </span>
                    }
                    rules={ValidationRules.taxNumber}
                    hasFeedback
                    validateStatus={validationStatus.taxNumber}
                  >
                    <Input
                      placeholder="1234567890"
                      maxLength={10}
                      onFocus={() => setFocusedField("taxNumber")}
                      onBlur={() => setFocusedField(null)}
                      style={{
                        borderRadius: "12px",
                        border: "2px solid #e2e8f0",
                        padding: "12px 16px",
                        fontSize: "16px",
                        background: "white",
                      }}
                      suffix={
                        validationStatus.taxNumber === "success" ? (
                          <CheckCircleOutlined
                            style={{ color: "#11998e", fontSize: "16px" }}
                          />
                        ) : null
                      }
                    />
                  </Form.Item>
                </div>
              </Col>

              <Col xs={24} lg={12}>
                <div
                  className={`modern-form-item ${
                    focusedField === "taxOffice" ? "focused" : ""
                  } ${
                    validationStatus.taxOffice === "success" ? "success" : ""
                  }`}
                >
                  <Form.Item
                    name="taxOffice"
                    label={
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontWeight: 600,
                          fontSize: "15px",
                          color: "#2d3748",
                        }}
                      >
                        <BankOutlined
                          style={{ color: "#667eea", fontSize: "16px" }}
                        />
                        Vergi Dairesi
                        <span
                          style={{
                            color: "#ff6b6b",
                            fontWeight: 700,
                            marginLeft: "4px",
                          }}
                        >
                          *
                        </span>
                      </span>
                    }
                    rules={[
                      { required: true, message: "Vergi dairesi zorunludur" },
                    ]}
                    hasFeedback
                    validateStatus={validationStatus.taxOffice}
                  >
                    <Input
                      placeholder="Örn: Kadıköy Vergi Dairesi"
                      onFocus={() => setFocusedField("taxOffice")}
                      onBlur={() => setFocusedField(null)}
                      style={{
                        borderRadius: "12px",
                        border: "2px solid #e2e8f0",
                        padding: "12px 16px",
                        fontSize: "16px",
                        background: "white",
                      }}
                      suffix={
                        validationStatus.taxOffice === "success" ? (
                          <CheckCircleOutlined
                            style={{ color: "#11998e", fontSize: "16px" }}
                          />
                        ) : null
                      }
                    />
                  </Form.Item>
                </div>
              </Col>

              <Col xs={24}>
                <div
                  className={`modern-form-item ${
                    focusedField === "address" ? "focused" : ""
                  } ${validationStatus.address === "success" ? "success" : ""}`}
                >
                  <Form.Item
                    name="address"
                    label={
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontWeight: 600,
                          fontSize: "15px",
                          color: "#2d3748",
                        }}
                      >
                        <span style={{ fontSize: "16px" }}>📍</span>
                        Şirket Adresi
                        <span
                          style={{
                            color: "#ff6b6b",
                            fontWeight: 700,
                            marginLeft: "4px",
                          }}
                        >
                          *
                        </span>
                      </span>
                    }
                    rules={[
                      { required: true, message: "Şirket adresi zorunludur" },
                      { min: 10, message: "Adres en az 10 karakter olmalıdır" },
                    ]}
                    hasFeedback
                    validateStatus={validationStatus.address}
                  >
                    <TextArea
                      placeholder="Tam adresinizi yazın (mahalle, sokak, bina no, kat, daire)"
                      rows={3}
                      showCount
                      maxLength={500}
                      onFocus={() => setFocusedField("address")}
                      onBlur={() => setFocusedField(null)}
                      style={{
                        borderRadius: "12px",
                        border: "2px solid #e2e8f0",
                        padding: "12px 16px",
                        fontSize: "16px",
                        background: "white",
                      }}
                    />
                  </Form.Item>
                </div>
              </Col>

              <Col xs={24}>
                <div
                  className={`modern-form-item ${
                    focusedField === "referralCode" ? "focused" : ""
                  }`}
                >
                  <Form.Item
                    name="referralCode"
                    label={
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontWeight: 600,
                          fontSize: "15px",
                          color: "#2d3748",
                        }}
                      >
                        <GiftOutlined
                          style={{ color: "#667eea", fontSize: "16px" }}
                        />
                        Referans Kodu
                        <span
                          style={{
                            color: "#8c8c8c",
                            fontSize: "12px",
                            fontWeight: 400,
                            marginLeft: "4px",
                          }}
                        >
                          (Opsiyonel)
                        </span>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="tooltip-referral">
                              Bir referans kodunuz varsa girebilirsiniz. Bu kod size özel avantajlar sağlayabilir.
                            </Tooltip>
                          }
                        >
                          <InfoCircleOutlined
                            style={{
                              color: "#4facfe",
                              fontSize: "14px",
                              marginLeft: "4px",
                              cursor: "help",
                            }}
                          />
                        </OverlayTrigger>
                      </span>
                    }
                  >
                    <Input
                      placeholder="Referans kodunu girin"
                      maxLength={50}
                      onFocus={() => setFocusedField("referralCode")}
                      onBlur={() => setFocusedField(null)}
                      style={{
                        borderRadius: "12px",
                        border: "2px solid #e2e8f0",
                        padding: "12px 16px",
                        fontSize: "16px",
                        background: "white",
                      }}
                    />
                  </Form.Item>
                </div>
              </Col>
            </Row>

            <Row gutter={[24, 0]} style={{ marginTop: "24px" }}>
              <Col xs={24}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <ReCAPTCHA
                    sitekey={
                      process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
                      "6Ld0pGsrAAAAACNcb-eidd8pO95j8f7p6JGcHZKO"
                    }
                    onChange={onRecaptchaChange}
                    onExpired={onRecaptchaExpired}
                    theme="light"
                    size="normal"
                    hl="tr" // Türkçe dil desteği
                  />
                </div>

                {recaptchaExpired && (
                  <Alert
                    message="reCAPTCHA süresi doldu"
                    description="Lütfen robot olmadığınızı tekrar doğrulayın."
                    type="warning"
                    showIcon
                    style={{ marginBottom: "16px", borderRadius: "12px" }}
                  />
                )}
              </Col>
            </Row>

            <div className="form-footer">
              <Button
                type="primary"
                htmlType="submit"
                icon={
                  otpLoading ? <LoadingOutlined spin /> : <ArrowRightOutlined />
                }
                loading={otpLoading}
                size="large"
                block
                disabled={formProgress < 100}
                style={{
                  height: "56px",
                  fontSize: "18px",
                  fontWeight: 600,
                  borderRadius: "16px",
                  background:
                    formProgress < 100
                      ? "#d1d5db"
                      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                  boxShadow:
                    formProgress < 100
                      ? "none"
                      : "0 8px 24px rgba(102, 126, 234, 0.4)",
                  color: "white",
                }}
              >
                {otpLoading ? (
                  <span style={{ color: "white" }}>Gönderiliyor...</span>
                ) : (
                  <span style={{ color: "white" }}>Devam Et</span>
                )}
              </Button>
            </div>
          </Form>
        </>
      );
    }

    if (otpStep === "otp-sent") {
      return (
        <>
          <div
            className="step-header"
            style={{
              background: "white",
              color: "#2d3748",
              // padding: '24px 0',
              // marginBottom: '24px',
              textAlign: "center",
            }}
          >
           
          </div>

          {/* Enhanced Success Alert */}
          <Alert
            message={
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "12px",
                }}
              >
                <CheckCircleOutlined
                  style={{ color: "#11998e", fontSize: "24px" }}
                />
                <span
                  style={{
                    fontSize: "18px",
                    fontWeight: 600,
                    color: "#11998e",
                  }}
                >
                  SMS Kodu Gönderildi!
                </span>
              </div>
            }
            description={
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <PhoneOutlined
                    style={{ color: "#4facfe", fontSize: "20px" }}
                  />
                  <div>
                    <Text strong style={{ color: "#2d3748", fontSize: "16px" }}>
                      {companyData?.phone}
                    </Text>
                    <br />
                    <Text style={{ color: "#718096", fontSize: "14px" }}>
                      {/* Kodu almadıysanız spam klasörünüzü kontrol edin veya birkaç dakika bekleyin. */}
                    </Text>
                  </div>
                </div>
              </div>
            }
            type="success"
            showIcon={false}
            style={{
              borderRadius: "16px",
              marginBottom: "30px",
              background:
                "linear-gradient(135deg, rgba(17, 153, 142, 0.05), rgba(56, 239, 125, 0.05))",
              border: "2px solid rgba(17, 153, 142, 0.2)",
            }}
          />

          <Form
            layout="vertical"
            onFinish={handleOtpSubmit}
            size="large"
            className="otp-form enhanced"
          >
            <Row justify="center">
              <Col xs={24} sm={20} md={18} lg={16}>
                <div style={{ textAlign: "center", marginBottom: "24px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i
                        className="ti ti-message-2"
                        style={{ color: "white", fontSize: "18px" }}
                      ></i>
                    </div>
                    <span
                      style={{
                        fontSize: "18px",
                        fontWeight: 600,
                        color: "#2d3748",
                      }}
                    >
                      SMS Doğrulama Kodu
                    </span>
                  </div>
                  <p style={{ color: "#64748b", fontSize: "14px", margin: 0 }}>
                    Telefon numaranıza gönderilen 6 haneli kodu giriniz
                  </p>
                </div>

                <Form.Item
                  name="otp"
                  validateTrigger={["onSubmit"]}
                  rules={[
                    {
                      validator: (_, value) => {
                        const otpString = otpValues.join("");
                        if (!otpString || otpString.length === 0) {
                          return Promise.reject(
                            new Error("Doğrulama kodu zorunludur")
                          );
                        }
                        if (otpString.length !== 6) {
                          return Promise.reject(
                            new Error("Doğrulama kodu 6 haneli olmalıdır")
                          );
                        }
                        if (!/^\d{6}$/.test(otpString)) {
                          return Promise.reject(
                            new Error("Sadece rakam girebilirsiniz")
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                  className="otp-form-item"
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "12px",
                      flexWrap: "wrap",
                    }}
                  >
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <Input
                        key={index}
                        id={`otp-input-${index}`}
                        maxLength={1}
                        value={otpValues[index]}
                        style={{
                          width: "56px",
                          height: "56px",
                          fontSize: "24px",
                          fontWeight: "bold",
                          textAlign: "center",
                          borderRadius: "12px",
                          border: "2px solid #e2e8f0",
                          background: "white",
                          color: "#2d3748",
                          transition: "all 0.3s ease",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                        }}
                        onChange={(e) => {
                          const value = e.target.value;

                          // Sadece rakam girişine izin ver
                          if (!/^\d*$/.test(value)) {
                            return;
                          }

                          // OTP values array'ini güncelle
                          const newOtpValues = [...otpValues];
                          newOtpValues[index] = value;
                          setOtpValues(newOtpValues);

                          // Form değerini güncelle
                          const otpString = newOtpValues.join("");
                          form.setFieldsValue({ otp: otpString });

                          // Otomatik ileri gitme
                          if (value && index < 5) {
                            const nextInput = document.getElementById(
                              `otp-input-${index + 1}`
                            );
                            if (nextInput) {
                              nextInput.focus();
                            }
                          }
                        }}
                        onKeyDown={(e) => {
                          // Backspace ile geri gitme
                          if (
                            e.key === "Backspace" &&
                            !otpValues[index] &&
                            index > 0
                          ) {
                            const prevInput = document.getElementById(
                              `otp-input-${index - 1}`
                            );
                            if (prevInput) {
                              prevInput.focus();
                            }
                          }

                          // Paste işlemi için
                          if (e.key === "v" && (e.ctrlKey || e.metaKey)) {
                            e.preventDefault();
                            navigator.clipboard.readText().then((text) => {
                              const digits = text
                                .replace(/\D/g, "")
                                .slice(0, 6);
                              if (digits.length > 0) {
                                const newOtpValues = ["", "", "", "", "", ""];
                                for (
                                  let i = 0;
                                  i < Math.min(digits.length, 6);
                                  i++
                                ) {
                                  newOtpValues[i] = digits[i];
                                }
                                setOtpValues(newOtpValues);
                                form.setFieldsValue({
                                  otp: newOtpValues.join(""),
                                });

                                // Son dolu input'a focus
                                const lastIndex = Math.min(
                                  digits.length - 1,
                                  5
                                );
                                const lastInput = document.getElementById(
                                  `otp-input-${lastIndex}`
                                );
                                if (lastInput) {
                                  lastInput.focus();
                                }
                              }
                            });
                          }
                        }}
                        onFocus={(e) => {
                          e.target.style.border = "2px solid #667eea";
                          e.target.style.boxShadow =
                            "0 0 0 3px rgba(102, 126, 234, 0.1)";
                        }}
                        onBlur={(e) => {
                          e.target.style.border = "2px solid #e2e8f0";
                          e.target.style.boxShadow =
                            "0 2px 4px rgba(0,0,0,0.05)";
                        }}
                        placeholder="●"
                      />
                    ))}
                  </div>
                </Form.Item>
              </Col>
            </Row>

            <div className="form-footer otp-footer">
              <Space
                direction="vertical"
                size="large"
                style={{ width: "100%" }}
              >
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={
                    loading ? <LoadingOutlined spin /> : <CheckCircleOutlined />
                  }
                  loading={loading}
                  size="large"
                  block
                  style={{
                    height: "56px",
                    fontSize: "16px",
                    fontWeight: 600,
                    borderRadius: "16px",
                    background:
                      "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                    border: "none",
                    color: "white",
                    boxShadow: "0 8px 24px rgba(17, 153, 142, 0.4)",
                  }}
                >
                  {loading ? (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        color: "white",
                      }}
                    >
                      <span style={{ color: "white" }}>
                        Şirket Oluşturuluyor
                      </span>
                      <span style={{ display: "flex", gap: "2px" }}>
                        <span style={{ color: "white" }}>.</span>
                        <span style={{ color: "white" }}>.</span>
                        <span style={{ color: "white" }}>.</span>
                      </span>
                    </span>
                  ) : (
                    <span style={{ color: "white" }}>
                      Kodu Doğrula ve Şirketi Oluştur
                    </span>
                  )}
                </Button>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "12px",
                  }}
                >
                  <Button
                    type="link"
                    onClick={handleResendOtp}
                    disabled={countdown > 0 || otpLoading}
                    loading={otpLoading}
                    icon={<SendOutlined />}
                    style={{
                      fontWeight: 500,
                      color:
                        countdown > 0 || otpLoading ? "#a0aec0" : "#4facfe",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {countdown > 0 ? (
                      <span
                        style={{ color: countdown > 0 ? "#a0aec0" : "#4facfe" }}
                      >
                        Tekrar gönder ({countdown}s)
                      </span>
                    ) : (
                      <span style={{ color: "#4facfe" }}>
                        Kodu Tekrar Gönder
                      </span>
                    )}
                  </Button>

                  <Button
                    type="link"
                    onClick={handleEditPhone}
                    icon={<EditOutlined />}
                    style={{
                      fontWeight: 500,
                      color: "#4facfe",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <span style={{ color: "#4facfe" }}>
                      Telefon Numarasını Değiştir
                    </span>
                  </Button>
                </div>
              </Space>
            </div>
          </Form>
        </>
      );
    }

    return null;
  };

  return (
    <Card
      className="form-card modern-card enhanced"
      loading={loading && otpStep === "otp-verified"}
      style={{
        borderRadius: "24px",
        boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        background: "white",
        backdropFilter: "blur(20px)",
        overflow: "hidden",
        position: "relative",
      }}
      bodyStyle={{
        padding: "40px",
        background: "white",
      }}
    >
      {renderFormContent()}
    </Card>
  );
};

export default React.memo(CompanyForm);
